const $$splitComponentImporter = () => import('/home/runner/work/zngly-corporate/zngly-corporate/wp-content/themes/zngly-corporate/frontend/src/routes/znglist.$slug.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
import { AccessControlWrapper } from '@/Components/posts/access-control';
import { getMediaItemUrl } from '@/Components/posts/utils';
import IconButtonLink from '@/Components/ui/Frontend/IconButtonLink';
import { __analyticsContentViewFix__ } from '@/Telemetry/infobox';
import { Chatbot } from '@/Utility/initApp/dialogflow-cx-messenger/chatbot';
import { __useDfMessenger__ } from '@/Utility/initApp/dialogflow-cx-messenger/useDfMessenger';
import { ZnglistFrontendSingleQuery, type Znglist_Frontend_Single_Query, type Znglist_Frontend_Single_Query$data, type Znglist_Frontend_Single_Query$variables } from '@/graphql/queries/Znglist.query';
import { RelayEnvironment } from '@/graphql/relay-env';
import { MediaItemSizeEnum, PostIdType, type Znglist, ZnglyPostTypeEnum, type ZnglyVideo } from '@/graphql/types';
import { useAppStore } from '@/inertia/AppStore';
import MainContainer, { shareButtonStyles } from '@/inertia/Shared/MainContainer';
import LandingPageTemplate from '@/inertia/Shared/Views/LandingPage/LandingPage.template';
import { createFileRoute, redirect, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { type PreloadedQuery, loadQuery, usePreloadedQuery } from 'react-relay';
import { z } from 'zod';
type TemplateType = 'classic' | 'personal';
type Props = {
  template?: TemplateType;
  data?: Znglist_Frontend_Single_Query$data;
  isFallback?: boolean;
};
export const ZnglistTemplateComponent = (props: Props) => {
  if (props?.template === 'personal' && props?.data && props?.data?.znglist?.znglist) {
    const znglist = props.data.znglist;
    const author = props?.data?.user?.name ? props?.data?.user : znglist?.author?.node;
    return <LandingPageTemplate title={znglist?.title ?? ''} profileImage={{
      src: getMediaItemUrl(author?.meta?.profileImage, MediaItemSizeEnum.Medium) ?? author?.avatar?.url ?? '',
      alt: author?.name ?? 'profile image'
    }} textArea={{
      name: `${author?.firstName} ${author?.lastName}`,
      role: `${author?.meta?.jobRole}`,
      description: `${author?.description}`
    }} socialIcons={author?.meta?.profileSocialLinks?.map((social, idx) => <IconButtonLink defaultIconPkg="fab" iconName={social?.name ?? ''} url={social?.url ?? ''} key={`${social?.name}_${idx}`} className={shareButtonStyles.socialBtn} />) ?? []} articleLinks={author?.meta?.profileArticleLinks?.map((article, idx) => <IconButtonLink key={`${article?.name}_${idx}`} className="article-link" iconName={article?.iconName ?? ''} name={article?.name} url={article?.url ?? ''} idx={idx} defaultIconPkg="fas" />) ?? []} />;
  }
  // classic
  return <MainContainer isFallback={props?.isFallback} />;
};
export type ZnglistLoaderData = PreloadedQuery<Znglist_Frontend_Single_Query, Znglist_Frontend_Single_Query$variables>;
type QueryLoaderProps = {
  loaderData: ZnglistLoaderData;
};
export const ZnglistTemplateLoader = (props: QueryLoaderProps) => {
  const setZnglist = useAppStore(state => state.setZnglist);
  const setPlayerNode = useAppStore(state => state.setPlayerNode);
  const setNodes = useAppStore(state => state.setNodes);
  const setBarTitle = useAppStore(state => state.setBarTitle);
  const data = usePreloadedQuery<Znglist_Frontend_Single_Query>(ZnglistFrontendSingleQuery, props.loaderData);
  const params = useParams({
    strict: false,
    select(state) {
      return {
        slug: state.slug ?? null
      };
    }
  });
  const search = useSearch({
    strict: false,
    select(state) {
      return {
        c: state.c ?? null,
        s: state.s ?? null
      };
    }
  });
  const navigate = useNavigate();
  __analyticsContentViewFix__();
  __useDfMessenger__({
    pageType: 'znglist',
    znglist: data?.znglist as Znglist
  });
  const catalogNodes = useAppStore(state => state.catalogNodes);
  const znglist = useAppStore(state => state.znglist);
  useEffect(() => {
    if (!catalogNodes?.length) return;
    let activeNodeIdx = 0;
    if (search?.c) {
      const foundIdx = catalogNodes.findIndex(node => node?.id === search?.c);
      if (foundIdx > -1) activeNodeIdx = foundIdx;
    }
    setPlayerNode(!!catalogNodes?.[activeNodeIdx] ? {
      ...catalogNodes?.[activeNodeIdx]
    } : null);
    if (!search?.c && catalogNodes?.[activeNodeIdx] || search?.c !== catalogNodes?.[activeNodeIdx]?.id) {
      navigate({
        // @ts-ignore
        search: {
          ...search,
          ...(catalogNodes?.[activeNodeIdx]?.id && {
            c: catalogNodes?.[activeNodeIdx]?.id
          })
        }
      });
    }
  }, [search?.c, catalogNodes]);
  const updatedZnglist = useRef(false);
  useEffect(() => {
    if (!data?.znglist || updatedZnglist.current) return;
    if (data?.znglist?.id === znglist?.id) return;
    const nodes = (data?.znglist?.znglist?.znglyContents ?? []).filter(node => !!node?.id).map(node => node as ZnglyVideo) ?? [];
    setZnglist({
      ...data?.znglist
    } as Znglist);
    setNodes([...nodes]);
    setBarTitle(data?.znglist?.znglist?.templateType?.toLowerCase?.() === 'personal' ? 'Znglist Content' : data?.znglist?.title ?? '');
    updatedZnglist.current = true;
    return () => {
      updatedZnglist.current = false;
    };
  }, [data?.znglist]);
  return <AccessControlWrapper id={data?.znglist?.id ?? null} input={{
    id: params.slug,
    idType: PostIdType.Slug,
    postType: ZnglyPostTypeEnum.Znglist
  }} onInitVerifyPostAccess={() => {
    Chatbot.setChatbotHidden(true);
  }} onOTPSuccess={() => {
    props.loaderData.dispose();
    Chatbot.setChatbotHidden(false);
    navigate({
      // @ts-ignore
      search: {
        inv: true
      }
    });
  }}>
      <ZnglistTemplateComponent template={data?.znglist?.znglist?.templateType as TemplateType} data={data} />
    </AccessControlWrapper>;
};
export const znglistSchema = z.object({
  c: z.string().optional().nullable().catch(null),
  s: z.string().optional().nullable().catch(undefined),
  inv: z.boolean().optional().nullable().catch(null)
});
export type ZnglistSearch = z.infer<typeof znglistSchema>;
export const Route = createFileRoute('/znglist/$slug')({
  component: lazyRouteComponent($$splitComponentImporter, 'component', () => Route.ssr),
  validateSearch: znglistSchema,
  loaderDeps(opts) {
    return {
      invalidate: !!opts?.search?.inv === true
    };
  },
  loader({
    params,
    deps
  }) {
    return loadQuery<Znglist_Frontend_Single_Query>(RelayEnvironment, ZnglistFrontendSingleQuery, {
      id: params.slug,
      idType: 'SLUG'
    }, {
      fetchPolicy: deps?.invalidate === true ? 'network-only' : 'store-or-network'
    });
  },
  beforeLoad: ({
    params
  }) => {
    if (!params.slug) {
      throw redirect({
        to: '/*'
      });
    }
  },
  onEnter: () => {
    const store = useAppStore.getState();
    store.resetInertia();
    store.setPageType('znglist');
    store.setBarView('list');
  }
});